import React, { useEffect, useRef, useState } from "react"
import { StyleSheet } from "react-native"

import CustomPressable from "../pressables/CustomPressable"
import CustomDialog, { ContentContainer } from "./CustomDialog"
import { CustomText } from "../text/StyledText"
import DatePicker from "../dates/DatePicker"
import { ButtonPrimary } from "../buttons/StyledButtons"

import NormalIcon from "../icons/NormalIcon"
import Colors from "../../constants/Colors"
import { txt } from "../../locales/i18n"
import { ShadowStyles } from "../../styles"
import { SnakSammenDuration } from "../views/snakSammen/volunteer/SnakSammenDurationSelector"

export default function EditSessionDialog({ visible, onDismiss, currentItem, reloadAll, updateSession, solutionType }) {
	if (!currentItem) return

	const solution = solutionType === 'SnakSammen' ? 'videoChat' : 'learnDanish'

	const [loading, setLoading] = useState(false)
	const [error, setError] = useState(false)

	const [startDate, setStartDate] = useState(new Date(currentItem.start_date))
	const minTime = useRef(new Date().setHours(5, 0, 0, 0))
	const maxTime = useRef(new Date().setHours(23, 0, 0, 0))
	const maximumDate = useRef(new Date().setMonth(new Date().getMonth() + 6))

	const [durationChosen, setDurationChosen] = useState(15)
	const [durationLabelChosen, setDurationLabelChosen] = useState(txt('videoChat.volunteer.create.duration15'))

	useEffect(() => {
		setStartDate(new Date(currentItem.start_date))
	}, [currentItem])

	function setMinTime() {
		const today = new Date()
		const isToday =
			startDate.getDate() === today.getDate() &&
			startDate.getMonth() === today.getMonth() &&
			startDate.getFullYear() === today.getFullYear()
		if (isToday) return new Date()
		return minTime.current
	}

	function handleDateChange(date, isTime) {
		if (!isTime) return setStartDate(date)

		setStartDate((startDate) => {
			const newDate = new Date(startDate)
			newDate.setHours(date.getHours(), date.getMinutes(), 0, 0)
			return newDate < new Date() ? new Date() : newDate
		})
	}

	function handleDurationChange(duration) {
		setDurationLabelChosen(duration.label)
		setDurationChosen(duration.value)
	}

	function handleErrorMessages(message) {
		const updateErrors = txt(`${solution}.volunteer.update`)

		const ERR_ENUMS = {
			SAME_DATES: updateErrors.SAME_DATES,
			DATE_EXPIRED: updateErrors.DATE_EXPIRED,
			SESSION_NOT_FOUND: updateErrors.SESSION_NOT_FOUND,
			NOT_AUTHORIZED: updateErrors.NOT_AUTHORIZED,
			INVALID_DATES: updateErrors.INVALID_DATES,
			VOLUNTEER_OR_ASSIGNED_ALREADY_BOOKED_IN_TIME_FRAME:
				updateErrors.VOLUNTEER_OR_ASSIGNED_ALREADY_BOOKED_IN_TIME_FRAME,
		}

		const errorMessage = ERR_ENUMS[message] || updateErrors.UNKNOWN_ERROR
		setError(errorMessage)
	}

	async function handleUpdateSession() {
		setLoading(true)

		const startDateTime = startDate.getTime()
		const endDateTime = startDateTime + durationChosen * 60 * 1000

		if (
			startDateTime === new Date(currentItem.start_date).getTime() &&
			endDateTime === new Date(currentItem.end_date).getTime()
		) {
			setLoading(false)
			return setError(txt(`${solution}.volunteer.update.SAME_DATES`))
		}

		if (startDateTime < Date.now() || startDateTime > endDateTime) {
			setLoading(false)
			return setError(txt(`${solution}.volunteer.update.INVALID_DATES`))
		}

		try {
			await updateSession({
				sessionId: currentItem.id,
				startDate: new Date(startDateTime),
				endDate: new Date(endDateTime),
			})
			setError(false)
			reloadAll()
			onDismiss()
		} catch (errorMsg) {
			handleErrorMessages(errorMsg)
		} finally {
			setLoading(false)
		}
	}

	const renderDatePickerCustomInput = ({ value, onClick }) => (
		<CustomPressable accessibilityRole="button" onPress={onClick} style={styles.datePickerCustomInput}>
			<CustomText style={{ fontFamily: 'gotham-book', fontSize: 15 }}>{value}</CustomText>
			<NormalIcon name="calendar-alt" size={14} color={Colors.inactive} />
		</CustomPressable>
	)

	return (
		<CustomDialog visible={visible} onDismiss={onDismiss} style={{ width: '95%' }}>
			<CustomPressable onPress={onDismiss} style={styles.closeIcon}>
				<NormalIcon name="times" size={20} color={Colors.darkGrey} />
			</CustomPressable>
			<ContentContainer>
				<CustomText font="gotham-bold" style={{ marginBottom: 12, fontSize: 22 }}>
					{txt(`${solution}.volunteer.update.updateSession`)}
				</CustomText>

				<CustomText font="gotham-bold-small" style={{ marginBottom: 12 }}>
					{txt('videoChat.volunteer.create.date')}
				</CustomText>

				<DatePicker
					date={startDate}
					minTime={setMinTime()}
					maxTime={maxTime.current}
					minDate={new Date()}
					maxDate={maximumDate.current}
					minuteInterval={5}
					handleDateChange={handleDateChange}
					textStyle={{ fontFamily: 'gotham-book', fontSize: 15 }}
					style={styles.picker}
					buttonStyle={[styles.dateButton, { marginTop: 12, marginLeft: 0 }]}
					buttonTitleStyle={styles.dateButtonTitle}
					CustomInput={renderDatePickerCustomInput}
				/>

				<SnakSammenDuration
					duration={durationLabelChosen}
					handleDurationChange={handleDurationChange}
					style={styles.pickerStyle}
				/>

				{error && (
					<CustomText font="gotham-small" style={styles.errorText}>
						{error}
					</CustomText>
				)}

				<ButtonPrimary
					title={txt(`${solution}.volunteer.update.updateButton`)}
					style={styles.button}
					titleStyle={styles.buttonTitle}
					onPress={handleUpdateSession}
					loading={loading}
				/>
			</ContentContainer>
		</CustomDialog>
	)
}

const styles = StyleSheet.create({
	closeIcon: {
		backgroundColor: Colors.greyLightLight,
		borderRadius: 12,
		padding: 8,
		position: 'absolute',
		top: 12,
		right: 12,
		zIndex: 1,
	},

	button: {
		borderRadius: 10,
		backgroundColor: Colors.redCross.background,
		paddingVertical: 13,
		paddingHorizontal: 32,
		minWidth: 115,
		marginTop: 24,
	},

	buttonTitle: {
		color: Colors.redCross.text,
		fontSize: 15,
		fontFamily: 'gotham-bold'
	},

	picker: {
		backgroundColor: Colors.white,
		borderWidth: 1,
		borderColor: Colors.redCrossGrey,
		borderRadius: 6,
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'space-between',
		...ShadowStyles.unsetShadows
	},

	datePickerCustomInput: {
		alignItems: 'center',
		flexDirection: 'row',
		justifyContent: 'space-between',
		borderWidth: 1,
		borderColor: Colors.redCrossGrey,
		borderRadius: 6,
		height: 40,
		paddingHorizontal: 10,
	},

	dateButton: {
		backgroundColor: Colors.redCross.red,
		borderRadius: 6,
		paddingVertical: 10,
		paddingHorizontal: 20,
	},

	dateButtonTitle: {
		color: Colors.white,
		fontSize: 14,
		fontFamily: 'gotham-bold'
	},

	errorText: {
		color: Colors.redCross.red,
		marginTop: 12
	},
})
